import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Phone, 
  Clock, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  History,
  Settings,
  Loader2,
  Calendar,
  PhoneCall,
  Shield,
  Users,
  LogOut,
  User,
  Lock,
  ChevronRight,
  AlertTriangle,
  UserX,
  Scale,
  CreditCard,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem('token');
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem('token', res.data.access_token);
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshUser = () => {
    fetchUser();
  };

  return { user, token, loading, login, register, logout, refreshUser };
};

// Landing Page
const LandingPage = ({ onShowAuth }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.15),transparent_50%)]" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Shield className="w-12 h-12 text-primary" />
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight font-mono">
                Probation Buddy
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
              Never miss a drug test again. Get daily voice call & email alerts for probation testing schedules.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              data-testid="get-started-btn"
              onClick={() => onShowAuth('register')}
              className="h-14 px-8 text-lg font-mono uppercase tracking-wider glow-blue"
            >
              Start Free Trial
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              data-testid="login-btn"
              variant="outline"
              onClick={() => onShowAuth('login')}
              className="h-14 px-8 text-lg font-mono uppercase tracking-wider"
            >
              Sign In
            </Button>
          </div>
          
          <p className="text-center text-muted-foreground mb-16">
            2-day free trial, then $10/month. Cancel anytime.
          </p>

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <Phone className="w-10 h-10 text-primary mb-2" />
                <CardTitle className="font-mono">Daily Alerts</CardTitle>
                <CardDescription>
                  Automated calls to the probation office every morning at 6 AM. Results sent to you instantly.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <PhoneCall className="w-10 h-10 text-primary mb-2" />
                <CardTitle className="font-mono">Voice & Email</CardTitle>
                <CardDescription>
                  Receive a phone call with your results plus a detailed email. Never miss an update.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <Card className="bg-card/50 border-border/50">
              <CardHeader>
                <Shield className="w-10 h-10 text-primary mb-2" />
                <CardTitle className="font-mono">Stay Informed</CardTitle>
                <CardDescription>
                  View local warrant lists and jail rosters. Know what's happening in your county.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Pricing */}
          <Card className="max-w-md mx-auto bg-card border-primary/50">
            <CardHeader className="text-center">
              <Badge className="w-fit mx-auto mb-2 bg-green-500/20 text-green-400 border-green-500/50">
                2-Day Free Trial
              </Badge>
              <CardTitle className="text-3xl font-mono">$10/month</CardTitle>
              <CardDescription>Try free for 2 days, then auto-renews monthly</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {[
                  "Daily drug testing alerts",
                  "Voice call notifications",
                  "Email notifications",
                  "Warrant list updates",
                  "Jail roster updates",
                  "Multiple county support (coming soon)",
                  "Calendar reminders (coming soon)"
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full mt-6 h-12 font-mono uppercase tracking-wider glow-blue"
                onClick={() => onShowAuth('register')}
              >
                Start Free Trial
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">
                No charge for 2 days. Cancel anytime before trial ends.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Probation Buddy • Serving All Nebraska Probation Districts</p>
          <p className="mt-2">Helping you stay compliant, one day at a time.</p>
        </div>
      </footer>
    </div>
  );
};

// Auth Page
const AuthPage = ({ mode, onModeChange, onLogin, onRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [district, setDistrict] = useState('');
  const [callTime, setCallTime] = useState('none');
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/districts`).then(res => setDistricts(res.data.districts)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await onLogin(email, password);
        toast.success('Welcome back!');
      } else {
        if (!email || !password) {
          toast.error('Please enter email and password');
          setLoading(false);
          return;
        }
        if (!phone) {
          toast.error('Please enter your phone number');
          setLoading(false);
          return;
        }
        if (!district) {
          toast.error('Please select your district');
          setLoading(false);
          return;
        }
        await onRegister({ 
          email, 
          password, 
          phone, 
          district: district,
          call_enabled: true,
          sms_enabled: true,
          preferred_call_time: callTime
        });
        toast.success('Account created!');
      }
    } catch (e) {
      console.error('Registration error:', e);
      toast.error(e.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(59,130,246,0.1),transparent_50%)]" />
      
      <Card className="w-full max-w-md relative">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-8 h-8 text-primary" />
            <span className="text-2xl font-mono font-bold">Probation Buddy</span>
          </div>
          <CardDescription>
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            
            {mode === 'register' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="4025551234"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                    required
                    data-testid="phone-input"
                  />
                  <p className="text-xs text-muted-foreground">We'll call and text this number daily with your results</p>
                </div>
                
                <div className="space-y-2">
                  <Label>District <span className="text-red-400">*</span></Label>
                  <Select value={district} onValueChange={setDistrict} required>
                    <SelectTrigger data-testid="district-select" className={!district ? "border-yellow-500/50" : ""}>
                      <SelectValue placeholder="Select your district" />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name} - {d.counties} ({d.city})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">You'll only receive results for your selected district</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Voice Call Notifications</Label>
                  <Select value={callTime} onValueChange={setCallTime}>
                    <SelectTrigger data-testid="calltime-select">
                      <SelectValue placeholder="Select call time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No voice calls (SMS & email only)</SelectItem>
                      <SelectItem value="06:00">6:00 AM</SelectItem>
                      <SelectItem value="06:15">6:15 AM</SelectItem>
                      <SelectItem value="06:30">6:30 AM</SelectItem>
                      <SelectItem value="07:00">7:00 AM</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="17:00">5:00 PM</SelectItem>
                      <SelectItem value="18:00">6:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">When should we call you with results? (optional)</p>
                </div>
              </>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-12 font-mono uppercase tracking-wider"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : mode === 'login' ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center text-sm">
            {mode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <button 
                  onClick={() => onModeChange('register')}
                  className="text-primary hover:underline"
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <button 
                  onClick={() => onModeChange('login')}
                  className="text-primary hover:underline"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard
const Dashboard = ({ user, onLogout, refreshUser }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard`);
      setDashboard(res.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check for payment success on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    
    if (sessionId && payment === 'success') {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setProcessingPayment(false);
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setProcessingPayment(false);
        if (refreshUser) refreshUser();
        fetchDashboard();
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired.');
        setProcessingPayment(false);
        return;
      }
      
      // Keep polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error('Payment status error:', e);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleSubscribe = async () => {
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, { origin_url: originUrl });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error('Failed to start checkout');
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const getResultBadge = (result) => {
    if (!result) return null;
    
    if (result.includes('NO TESTING')) {
      return (
        <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-lg px-4 py-2">
          <CheckCircle2 className="w-5 h-5 mr-2" />
          NO TESTING TODAY
        </Badge>
      );
    } else if (result.includes('TESTING REQUIRED')) {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-lg px-4 py-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          TESTING REQUIRED
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-lg px-4 py-2">
          <AlertCircle className="w-5 h-5 mr-2" />
          CHECK TRANSCRIPT
        </Badge>
      );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const getTimeUntilNextCall = () => {
    if (!dashboard?.next_scheduled_call) return null;
    const next = new Date(dashboard.next_scheduled_call);
    const now = new Date();
    const diff = next - now;
    if (diff <= 0) return 'Imminent';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Paywall - show subscription required if not active
  if (user?.subscription_status !== 'active') {
    return (
      <div className="min-h-screen bg-background">
        <Toaster richColors position="top-right" />
        
        <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.15),transparent_40%)] pointer-events-none" />
        
        <div className="relative max-w-2xl mx-auto p-4 md:p-8">
          {/* Header */}
          <header className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight font-mono">Probation Buddy</h1>
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </header>

          {/* Paywall Card */}
          <Card className="bg-card border-primary/50 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="w-10 h-10 text-primary" />
              </div>
              <CardTitle className="text-2xl font-mono">Start Your Free Trial</CardTitle>
              <CardDescription className="text-base">
                Enter your card to unlock all features. You won't be charged for 2 days.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* What you get */}
              <div className="bg-secondary/30 rounded-lg p-4">
                <h3 className="font-semibold mb-3">What's included:</h3>
                <ul className="space-y-2">
                  {[
                    "Daily drug testing alerts at 6 AM",
                    "Voice call notifications",
                    "Email notifications",
                    "Warrant list access",
                    "Jail roster access"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing */}
              <div className="text-center">
                <p className="text-3xl font-mono font-bold text-primary">$10/month</p>
                <p className="text-sm text-muted-foreground mt-1">after 2-day free trial</p>
              </div>

              {/* CTA Button */}
              <Button 
                onClick={handleSubscribe}
                className="w-full h-14 text-lg font-mono uppercase tracking-wider glow-blue"
                disabled={processingPayment}
                data-testid="subscribe-btn"
              >
                {processingPayment ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><CreditCard className="w-5 h-5 mr-2" /> Start Free Trial</>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Cancel anytime before trial ends. No charge if you cancel within 2 days.
              </p>
            </CardContent>
          </Card>

          {/* Your Info */}
          <Card className="mt-4 bg-card/50 border-border/50">
            <CardContent className="py-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Signed in as</span>
                <span>{user?.email}</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-2">
                <span className="text-muted-foreground">Phone</span>
                <span className="font-mono">{user?.phone}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-right" />
      
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.15),transparent_40%)] pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight font-mono">Probation Buddy</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden md:block">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </header>

        <Tabs defaultValue="testing" className="space-y-6">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="testing" className="font-mono">Testing</TabsTrigger>
            <TabsTrigger value="warrants" className="font-mono">Warrants</TabsTrigger>
            <TabsTrigger value="inmates" className="font-mono">Jail</TabsTrigger>
            <TabsTrigger value="settings" className="font-mono">Settings</TabsTrigger>
          </TabsList>

          {/* Testing Tab */}
          <TabsContent value="testing">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {/* Main Status Card */}
              <Card className="col-span-full md:col-span-8 bg-card border-border/50 shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-mono flex items-center gap-2">
                    <Phone className="w-6 h-6 text-primary" />
                    Drug Testing Status
                  </CardTitle>
                  <CardDescription>
                    {dashboard?.district?.name || 'District 6'} - {dashboard?.district?.counties || 'Nebraska'}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {dashboard?.latest_call ? (
                    <>
                      <div className="text-center py-8">
                        {getResultBadge(dashboard.latest_call.testing_result)}
                        <p className="text-sm text-muted-foreground mt-4">
                          Last updated: {formatDate(dashboard.latest_call.created_at)}
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-mono">
                          Transcript
                        </h3>
                        <ScrollArea className="h-40 rounded-lg bg-secondary/50 p-4">
                          <p className="text-sm whitespace-pre-wrap">
                            {dashboard.latest_call.transcript || 'No transcript available'}
                          </p>
                        </ScrollArea>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No calls recorded yet</p>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <p className="text-sm text-muted-foreground">
                      Results updated daily at {dashboard?.district?.call_time === '05:30' ? '5:30' : '6:00'} AM Central
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Side Cards */}
              <div className="col-span-full md:col-span-4 space-y-4">
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-mono flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      Next Call
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-mono font-bold text-primary text-center">
                      {getTimeUntilNextCall()}
                    </p>
                    <p className="text-sm text-muted-foreground text-center mt-2">
                      Daily at 6:00 AM Central
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-mono flex items-center gap-2">
                      <User className="w-5 h-5 text-primary" />
                      Your Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone</span>
                      <span className="font-mono">{user?.phone}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">District</span>
                      <span>{dashboard?.district?.name || `District ${user?.district}`}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Subscription</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                        Active
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Warrants Tab */}
          <TabsContent value="warrants">
            <a 
              href="https://fremont-pd-ne.zuercherportal.com/#/warrants" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-card border-border/50 hover:border-primary/50 transition-all cursor-pointer group">
                <CardContent className="p-0">
                  <div className="relative h-64 md:h-80 bg-gradient-to-br from-red-900/20 to-red-600/10 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                      <Scale className="w-20 h-20 text-red-400 mb-4 group-hover:scale-110 transition-transform" />
                      <h2 className="text-3xl font-mono font-bold text-white mb-2">Active Warrants</h2>
                      <p className="text-lg text-muted-foreground mb-4">Dodge County, Nebraska</p>
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-sm px-4 py-2">
                        Click to View Full List
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 right-4 text-xs text-muted-foreground">
                      Source: Fremont Police Department
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          </TabsContent>

          {/* Inmates Tab */}
          <TabsContent value="inmates">
            <a 
              href="https://fremont-pd-ne.zuercherportal.com/#/inmates" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <Card className="bg-card border-border/50 hover:border-primary/50 transition-all cursor-pointer group">
                <CardContent className="p-0">
                  <div className="relative h-64 md:h-80 bg-gradient-to-br from-blue-900/20 to-blue-600/10 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                      <Users className="w-20 h-20 text-blue-400 mb-4 group-hover:scale-110 transition-transform" />
                      <h2 className="text-3xl font-mono font-bold text-white mb-2">Jail Roster</h2>
                      <p className="text-lg text-muted-foreground mb-4">Dodge County, Nebraska</p>
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50 text-sm px-4 py-2">
                        Click to View Current Inmates
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 right-4 text-xs text-muted-foreground">
                      Source: Fremont Police Department
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-card border-border/50 max-w-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-mono flex items-center gap-2">
                  <Settings className="w-6 h-6 text-primary" />
                  Notification Settings
                </CardTitle>
                <CardDescription>Control how and when you receive updates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Voice Call Notifications</Label>
                  <Select 
                    value={user?.preferred_call_time || 'none'} 
                    onValueChange={async (value) => {
                      try {
                        await axios.put(`${API}/auth/preferences`, { preferred_call_time: value });
                        toast.success(value === 'none' ? 'Voice calls disabled' : `Call time set to ${value}`);
                        if (refreshUser) refreshUser();
                      } catch (e) {
                        toast.error('Failed to update');
                      }
                    }}
                  >
                    <SelectTrigger data-testid="call-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Disabled (SMS & email only)</SelectItem>
                      <SelectItem value="06:00">6:00 AM</SelectItem>
                      <SelectItem value="06:15">6:15 AM</SelectItem>
                      <SelectItem value="06:30">6:30 AM</SelectItem>
                      <SelectItem value="07:00">7:00 AM</SelectItem>
                      <SelectItem value="08:00">8:00 AM</SelectItem>
                      <SelectItem value="09:00">9:00 AM</SelectItem>
                      <SelectItem value="12:00">12:00 PM</SelectItem>
                      <SelectItem value="17:00">5:00 PM</SelectItem>
                      <SelectItem value="18:00">6:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {user?.preferred_call_time && user?.preferred_call_time !== 'none' 
                      ? `You'll receive a call at ${user.preferred_call_time} with your results` 
                      : 'Voice calls are disabled. You\'ll still get SMS and email.'}
                  </p>
                </div>
                
                <Separator />
                
                {/* Test Call Button */}
                <div className="bg-primary/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <PhoneCall className="w-4 h-4" />
                        Test Voice Call
                      </p>
                      <p className="text-sm text-muted-foreground">Send yourself a test call to hear what it sounds like</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          toast.info('Initiating test call...');
                          const res = await axios.post(`${API}/test-call`);
                          toast.success(res.data.message);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to send test call');
                        }
                      }}
                      data-testid="test-call-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call Me
                    </Button>
                  </div>
                </div>
                
                <Separator />
                
                {/* SMS Notifications */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">SMS Text Messages</p>
                    <p className="text-sm text-muted-foreground">Receive daily text with results</p>
                  </div>
                  <Button
                    variant={user?.sms_enabled !== false ? "default" : "outline"}
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/auth/preferences`, { sms_enabled: !user?.sms_enabled });
                        toast.success(user?.sms_enabled !== false ? 'SMS disabled' : 'SMS enabled');
                        if (refreshUser) refreshUser();
                      } catch (e) {
                        toast.error('Failed to update');
                      }
                    }}
                    data-testid="toggle-sms-btn"
                  >
                    {user?.sms_enabled !== false ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                
                {/* Test SMS Button */}
                <div className="bg-green-500/10 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Test SMS
                      </p>
                      <p className="text-sm text-muted-foreground">Send yourself a test text message</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          toast.info('Sending test SMS...');
                          const res = await axios.post(`${API}/test-sms`);
                          toast.success(res.data.message);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to send test SMS');
                        }
                      }}
                      data-testid="test-sms-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Text Me
                    </Button>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-muted-foreground">Always sent to {user?.email}</p>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Always On</Badge>
                </div>
                
                <Separator />
                
                <div className="bg-secondary/30 rounded-lg p-4">
                  <p className="font-medium mb-2">Your Account</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone</span>
                      <span className="font-mono">{user?.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">District</span>
                      <span>District {user?.district}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Active</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <footer className="mt-8 text-center text-xs text-muted-foreground">
          <p>Probation Buddy • Automated at 6:00 AM Central Daily</p>
        </footer>
      </div>
    </div>
  );
};

// Main App
function App() {
  const { user, loading, login, register, logout, refreshUser } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    return <Dashboard user={user} onLogout={logout} refreshUser={refreshUser} />;
  }

  if (showAuth) {
    return (
      <AuthPage 
        mode={authMode} 
        onModeChange={setAuthMode}
        onLogin={login}
        onRegister={register}
      />
    );
  }

  return (
    <>
      <Toaster richColors position="top-right" />
      <LandingPage onShowAuth={(mode) => { setAuthMode(mode); setShowAuth(true); }} />
    </>
  );
}

export default App;
